import { createSequence, queryString } from '@olivierpascal/helpers';
import { Button } from '@sixui/ui';
import { useTranslation } from 'react-i18next';

import type { IContainerProps } from '~/components/utils/Container';
import { ContentBlock } from '~/components/molecules/ContentBlock';
import { ErrorMessage } from '~/components/molecules/ErrorMessage';
import { Table } from '~/components/molecules/Table';
import {
  OrganizationInvitationListItem,
  OrganizationInvitationListItemSkeleton,
} from '~/components/organisms/OrganizationInvitationListItem';
import { useOrganizationInvitationsQuery } from '~/lib/api/modules/Invitations/hooks/useOrganizationInvitationsQuery';

const GRID_TEMPLATE_COLUMNS = 'repeat(3, 1fr) minmax(140px, min-content)';
const DEFAULT_SKELETON_COUNT = 3;

export type IOrganizationInvitationListProps = IContainerProps & {
  organizationId: string;
};

export const OrganizationInvitationList: React.FC<
  IOrganizationInvitationListProps
> = (props) => {
  const { sx, organizationId } = props;
  const { t } = useTranslation();
  const organizationInvitationsQuery = useOrganizationInvitationsQuery({
    organizationId,
  });
  const organizationInvitations = organizationInvitationsQuery.data?.items;
  const isEmpty =
    !organizationInvitationsQuery.isLoading && !organizationInvitations?.length;

  return (
    <ContentBlock sx={sx}>
      {organizationInvitationsQuery.isError ? (
        <ContentBlock.Body first>
          <ErrorMessage error={organizationInvitationsQuery.error} />
        </ContentBlock.Body>
      ) : isEmpty ? (
        <ContentBlock.Empty title={t('OrganizationInvitationList.empty')}>
          <Button
            href={`/organizations/${organizationId}/invitations/create?${queryString.build(
              { previousUrl: `/organizations/${organizationId}/invitations` },
            )}`}
          >
            {t('OrganizationInvitationList.create-first')}
          </Button>
        </ContentBlock.Empty>
      ) : (
        <Table gridTemplateColumns={GRID_TEMPLATE_COLUMNS}>
          <Table.Header>{t('common.emailAddress')}</Table.Header>
          <Table.Header>{t('common.role')}</Table.Header>
          <Table.Header>{t('common.status')}</Table.Header>
          <Table.Header />
          {organizationInvitationsQuery.isLoading
            ? createSequence(DEFAULT_SKELETON_COUNT).map((index) => (
                <OrganizationInvitationListItemSkeleton key={index} />
              ))
            : organizationInvitations?.map((invitation) => (
                <OrganizationInvitationListItem
                  key={invitation._id}
                  organizationId={organizationId}
                  invitation={invitation}
                />
              ))}
        </Table>
      )}
    </ContentBlock>
  );
};
